import {makeAutoObservable} from 'mobx';
import type {Bag, DataBagSerializer} from "../services/classes/Bag";
import type {MithraMaterializedApi} from "../services/MithraMaterializedApi";
import type ProfileStore from "./ProfileStore";
import {stores} from "./index";
import type {Location, NavigateFunction} from "react-router";
import {BagKpiManager} from "./managers/BagKpiManager";
import {BusinessUnitManager} from "./managers/BusinessUnitManager";
import {generateGotoDashboardPath} from "../routing/routing.utils";
import {BagAdvancedInsightsManager} from "./managers/BagAdvancedInsightsManager";
import type AuthStore from "../authentication/AuthStore";
import {InsightsJobManager} from "./managers/InsightsJobManager";
import {DatabagConfigStore} from "./config/DatabagConfigStore";
import type {ColumnConfiguration, CustomerConfigurationMap, DatabagConfiguration} from "./config/DatabagConfigClasses";
import {SynergyStore} from "./SynergyStore";
import type {Observable, Subscription} from "rxjs";
import {from} from "rxjs";

export class BagStore {

    bag: Bag | undefined;
    allBags?: Bag[] = undefined;
    $getAllBags?: Subscription;

    public readonly kpi: BagKpiManager;
    public readonly bu: BusinessUnitManager;
    public readonly advanced: BagAdvancedInsightsManager;
    public readonly advanced_job: InsightsJobManager;
    public readonly synergy: SynergyStore;
    public readonly configStore: DatabagConfigStore;

    constructor(
        public readonly api: MithraMaterializedApi,
        public readonly profileStore: ProfileStore,
        public readonly authStore: AuthStore,
    ) {
        makeAutoObservable(this);
        this.configStore = new DatabagConfigStore(this.api, this.authStore, profileStore)
        this.kpi = new BagKpiManager(this.api, this, this.profileStore)
        this.bu = new BusinessUnitManager(this.profileStore, this.api)
        this.synergy = new SynergyStore(this.api, this, this.profileStore)
        this.advanced = new BagAdvancedInsightsManager(this.api, this, this.profileStore)
        this.advanced_job = new InsightsJobManager(this.api)
    }

    get userBagChoices(): Bag[] | undefined {
        if (!this.allBags) return undefined;
        let bags = this.allBags
        if (!this.authStore.isMithraStaffGroup) {
            bags = bags.filter(b => !b.hidden)
        }
        return bags;
    }

    get userBagDefaultOption(): Bag | null | undefined {
        const bags = this.userBagChoices;
        if (bags === undefined) return undefined;
        const lastHiddenDatabagI = bags.slice().reverse().findIndex(b => !b.hidden);
        const bag = lastHiddenDatabagI === -1 ? bags.at(-1) : bags.at(bags.length - lastHiddenDatabagI - 1);
        return bag ?? null;
    }

    setAllBags(allBags: Bag[]) {
        this.allBags = allBags;
    }

    get bagId(): number {
        return this.bag?.id || 0;
    }

    get customerConfig(): CustomerConfigurationMap | null {
        return this.configStore.customerConfig;
    }

    get customerConfigLocale(): string | null {
        const val = this.customerConfig?.get('LOCALE')?.value;
        return val ? String(val) : null;
    }

    get customerConfigCurrency(): string | null {
        const val = this.customerConfig?.get('CURRENCY')?.value;
        return val ? String(val) : null;
    }

    get databagConfig(): DatabagConfiguration | null {
        return (this.configStore.databagConfig && this.configStore.databagConfig.databag_id === this.bagId) ? this.configStore.databagConfig : null;
    }

    get apiColumns(): ColumnConfiguration[] {
        return (this.databagConfig?.columns ?? []).filter(c => c.in_use)
    }

    get effectiveColumns(): ColumnConfiguration[] {
        // CAT-2731: this override is meant to use categorizationReviewSubRowRelationData as default
        if (this.profileStore.p.categorizationReviewSubRowRelationData) {
            // DO NOT REMOVE! Something strange is happening here. If we remove the following, somehow
            //  SCTransactionDetailTableComponent wouldn't update its reference of datasource of MUI datagrid
            //  This needs further investigation
            console.debug('categorizationColumns',
                JSON.parse(JSON.stringify(this.profileStore.p.categorizationReviewSubRowRelationData)),
                JSON.parse(JSON.stringify(this.apiColumns)));
            // Set columns that are active in the ColumnConfiguration, but not present in customer's config file
            const configuredInEnvCols: string[] = [];
            this.profileStore.p.categorizationReviewSubRowRelationData.forEach(rd =>
                configuredInEnvCols.push(...(rd.columns.map(rdc => rdc.db_column)))
            );
            const result: ColumnConfiguration[] = [...(this.apiColumns.filter(c => !configuredInEnvCols.includes(c.column_name)))];
            const default_kv = {
                stat_count: 0,
                stat_unique: 0,
                stat_empty: 0,
                stat_null: 0,
                stat_is_number: 0,
                stat_min: '',
                stat_max: '',
                description: '',
                in_use: true,
                default_visibility: true,
                mh_in_use: false,
                mh_default_visibility: false,
                default_order: null,
                width: null,
                min_width: null,
                max_width: null,
            }
            this.profileStore.p.categorizationReviewSubRowRelationData.forEach(relation_data => {
                relation_data.columns.forEach(column => {
                    result.push({
                        table_name: `data_source_${relation_data.relation}`,
                        column_name: column.db_column,
                        display_name: column.colSpec?.txt ?? '',
                        ...default_kv,
                    } satisfies ColumnConfiguration);
                });
            });

            return result;
        } else {
            return this.apiColumns;
        }
    }

    get categorizationColumns(): ColumnConfiguration[] {
        return this.effectiveColumns.filter(c =>
            ['data_source_bussines_unit', 'data_source_supplier', 'data_source_part', 'm_review_matpartreviewrow'].includes(c.table_name));
    }

    get isSample(): boolean {
        return Boolean(this.bag?.is_sample_of);
    }

    /**
     * Experiment: We use a sample bag to initialize the categorization review page
     */
    get sampleDatabagId(): number | null {
        const samples = this.bag?.databag_samples ?? [];
        if (samples.length > 0) {
            return samples.at(-1)!;
        }
        return null;
    }

    set(b: Bag) {
        // TODO: We do not need a set() and setBag() function here...
        this.bag = b;
        this.kpi.init(b);
        this.bu.init(b);
        if (!this.profileStore.p.disablePreloadAdvDashboard) {
            this.advanced.setDatabagId(this.bag.id);
        }
        this.advanced_job.updateRunningCount();
        this.configStore.setDatabagConfiguration(b.id)
        this.synergy.getSynergySrcs(b.id);
    }

    setBag(b: Bag) {
        // console.debug('BagStore: setBag', toJS(b))
        this.set(b);

        // TODO: Remove hack (Before we used hardcodeTaxonomyId for SHV we use masterTaxonomyId)
        const currentTaxonomyId = this.profileStore.p.masterTaxonomyId
            ? this.profileStore.p.masterTaxonomyId
            : b.taxonomy;

        stores.categorizationReviewStore.init(this.bagId, currentTaxonomyId, this.taxonomy_size, this.isSample);
    }

    changeBag(newBagId: unknown | number, navigate?: NavigateFunction, location?: Location) {
        const newBag = this.allBags?.find(b => b.id === newBagId) as Bag
        if (!newBag) {
            console.error('Cannot change bag to bagId:', newBagId)
            return;
        }
        if (navigate && location)
            navigate(generateGotoDashboardPath(location, {id: newBag.id}));
        this.setBag(newBag);
    }

    patch(b: Bag) {
        this.bag = b; // Optimistic caching used here: Consider TanStack Query
        const data: Partial<DataBagSerializer> = {name: b.name, hidden: b.hidden};
        return this.api.http.patch<Bag>(`/bag/${b.id}/`, data)
    }

    getAllBags(): Observable<Bag[]> {
        if (this.$getAllBags) {
            this.$getAllBags.unsubscribe();
        }
        const observable = from(this.api.getAllBags());
        this.$getAllBags = observable.subscribe({
            next: (bags) => this.setAllBags(bags),
            error: (error) => {
                console.error("Error fetching all bags:", error);
            },
            complete: () => {
                this.$getAllBags = undefined;
            }
        })
        return observable;
    }


    get taxonomy_size() {
        return this.bag?.taxonomy_size || 3;
    }

    getBagName(databagId: number): string {
        return this.allBags?.find(bag => bag.id === databagId)?.name ?? '';
    }
}
